import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import ActionPanel from "./ActionPanel";
import Button from "../button/Button";
import { POSITION, POSITION_MAP } from "../../constant/position";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetProjectQuery } from "../../app/services/projects";

/**
 * プロジェクト内の主アクション（PRODUCER「提出」/ CLIENT「資料を送る」）。
 *
 * DM の「成果物を提出」と同じ全幅ボタンを、コメントフィールド（や下部パネル）の
 * 上に置く。置き場所を持つページは `ProjectPrimaryActionSlot` をその先頭に描画し、
 * 表示中の slot が 1 つも無いときは ProjectWrapper が `ProjectPrimaryActionBar`
 * （BottomAppBar の上の行）に出す。
 *
 * 表示するタブは PRIMARY_ACTION_BY_POSITION の tabs のみ（提出はホーム、
 * 資料を送るはホームとトークルーム）。DM は契約フローのボタン（成果物を提出 /
 * 検収）と混同するため、プロジェクトの設定・ノート・ライブラリ・イメージボード・
 * Preview は作業面を覆うため出さない。
 * 実行（提出モーダル / ファイル選択）は PrimaryActionHandler が担う。
 */

/** ポジション別のラベルと出すタブ（提出はホームの成果物へ、資料はトークルームへ送る） */
const PRIMARY_ACTION_BY_POSITION: Record<number, { label: string; tabs: Set<string> }> = {
  [POSITION.CLIENT]: { label: "資料を送る", tabs: new Set(["home", "talkroom"]) },
  [POSITION.PRODUCER]: { label: "提出", tabs: new Set(["home"]) },
};

interface PrimaryActionContextValue {
  /** 表示するラベル。null なら現在のタブでは主アクションを出さない */
  label: string | null;
  trigger: () => void;
  /** slot の登録（戻り値で解除） */
  registerSlot: () => () => void;
}

export const PrimaryActionContext = createContext<PrimaryActionContextValue>({
  label: null,
  trigger: () => {},
  registerSlot: () => () => {},
});

interface UseProjectPrimaryActionOptions {
  projectId: string | undefined;
  activeTab: string;
  isPreviewRoute: boolean;
  onTrigger: () => void;
}

/**
 * ProjectWrapper 用。Provider に渡す値と、フォールバックの行を出すかを返す。
 */
export const useProjectPrimaryAction = ({
  projectId,
  activeTab,
  isPreviewRoute,
  onTrigger,
}: UseProjectPrimaryActionOptions) => {
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId ?? "", {
    skip: !projectId || !currentUserData,
  });
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );

  const positionStr: string | undefined = membershipData?.data?.position;
  const userPosition = positionStr
    ? (POSITION_MAP as Record<string, number>)[positionStr] ?? null
    : null;
  // 納品物なし（チェックポイント0）は PRODUCER の「提出」先（ホーム）が無い。
  // CLIENT の「資料を送る」はトークルームへの送信なので維持する。
  const noDeliverable = projectData?.data?.project?.total_shots_count === 0;
  const config =
    userPosition && !(noDeliverable && userPosition === POSITION.PRODUCER)
      ? PRIMARY_ACTION_BY_POSITION[userPosition] ?? null
      : null;

  const label =
    config && !isPreviewRoute && config.tabs.has(activeTab) ? config.label : null;

  const [slotCount, setSlotCount] = useState(0);
  const registerSlot = useCallback(() => {
    setSlotCount((count) => count + 1);
    return () => setSlotCount((count) => count - 1);
  }, []);

  const value = useMemo(
    () => ({ label, trigger: onTrigger, registerSlot }),
    [label, onTrigger, registerSlot],
  );

  return { value, showBar: !!label && slotCount === 0 };
};

const PrimaryActionButton: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <div className="u-col u-gap8 u-w100 u-pb8">
    <Button
      size="medium"
      variant="filled"
      theme="primary"
      width="full"
      rightIcon="chevron_right"
      onClick={onClick}
    >
      {label}
    </Button>
  </div>
);

interface ProjectPrimaryActionSlotProps {
  /**
   * 登録だけしてボタンは描画しない。置き場所（下部パネル）に同等の操作が
   * 既にあり、フォールバックの行も出したくない場合に使う。
   */
  hidden?: boolean;
}

/**
 * コメントフィールドや下部パネルの先頭に置く主アクション。
 * マウント中は登録し、フォールバックの行（ProjectPrimaryActionBar）を抑止する。
 */
export const ProjectPrimaryActionSlot: React.FC<ProjectPrimaryActionSlotProps> = ({
  hidden = false,
}) => {
  const { label, trigger, registerSlot } = useContext(PrimaryActionContext);
  const enabled = !!label;

  useEffect(() => {
    if (!enabled) return undefined;
    return registerSlot();
  }, [enabled, registerSlot]);

  if (!label || hidden) return null;
  return <PrimaryActionButton label={label} onClick={trigger} />;
};

/**
 * slot が無い画面で BottomAppBar の上の行に出す主アクション。
 * 幅はコメントフィールド（最大 740px）に揃える。
 */
export const ProjectPrimaryActionBar: React.FC = () => {
  const { label, trigger } = useContext(PrimaryActionContext);
  if (!label) return null;
  return (
    <ActionPanel>
      <div className="u-w100" style={{ maxWidth: 740 }}>
        <PrimaryActionButton label={label} onClick={trigger} />
      </div>
    </ActionPanel>
  );
};
